/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState, useEffect } from "react";
import { connect } from "react-redux";
import { createStyles } from "@material-ui/core/styles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import classNames from "classnames";

import { Dialog, DialogContent, Button, Fab } from "@material-ui/core";
import {
  Close,
  SkipNextRounded,
  SkipPreviousRounded,
} from "@material-ui/icons";

import { getLicensedDoc, ApricotAction } from "client/actions/infobox.ts";
import { selectorPDFContent } from "client/selectors/infoboxSelector";
import { ScreenInfo } from "client/reducers/modalReducer";

import { PdfLib } from "./PdfLib";
import { PdfViewer } from "./PDFViewer";
import { progressCircles } from "components/Form/FormField";
import {
  idCard,
  idCardFlip,
  idClose,
  idCardNext,
  idCardPrev,
} from "assets/jss/views/ElementIDs";

import cardViewerStyle from "assets/jss/views/infobox/cardViewerStyle";
const styles = createStyles(cardViewerStyle);

interface Props extends WithStyles<typeof styles> {
  getLicensedDoc: ApricotAction;
  // store
  pdfContent: any;
  screenInfo: ScreenInfo;
  // component props
  contentLink: string;
  onAction: (params: object) => void;
  classes: any;
}

const CardViewerBase: React.FC<Props> = ({
  getLicensedDoc,
  pdfContent,
  screenInfo,
  contentLink,
  onAction,
  classes,
}: Props) => {
  const [pdfjsLib, setPdfJsLib] = useState<PdfLib>();
  const [triggerFlip, setTriggerFlip] = useState(false);
  const [docDimension, setDocDimension] = useState<{
    count: number;
    width: number;
    height: number;
    scale: number;
  }>({ count: 0, width: 400, height: 240, scale: 1 });
  const [pdfReady, setPdfReady] = useState(false);
  const [cardNum, setCardNum] = useState(0);

  useEffect(() => {
    if (contentLink) {
      setPdfJsLib(new PdfLib());
      getLicensedDoc({
        docpath: contentLink,
        cr: false, // no copy right text
      });
    }
  }, []);

  useEffect(() => {
    // When matching content is loaded, and pdfLib is ready, set up the doc inside
    // pdfLib and also get the page width/count
    if (pdfContent && pdfContent.contentLink === contentLink && pdfjsLib) {
      (async function getInfo() {
        const { pageWidth, pageHeight, pageCount } = await pdfjsLib.setPdfDoc(
          pdfContent,
        );
        // decide how much width we use on the device
        let ratio = 1.0;

        // for small landscape
        if (screenInfo.orientation === "landscape") {
          const w = (screenInfo.height - 80) * 1.65;
          if (screenInfo.width > w) {
            ratio = w / screenInfo.width;
          }
        }
        if (screenInfo.width >= 1280) ratio = 0.6;
        else if (screenInfo.width >= 1024) ratio = 0.8;
        else if (screenInfo.width >= 768) ratio = 0.9;

        const scale = Math.min(
          Math.floor(((window.innerWidth * ratio) / pageWidth) * 10),
          Math.floor(((window.innerHeight * ratio) / pageHeight) * 10),
        );

        const width = (pageWidth * scale) / 10;
        const height = Math.floor(width / 1.65);

        setDocDimension({ width, height, scale, count: pageCount });
        setPdfReady(true);
      })();
    }
  }, [pdfContent, pdfjsLib, screenInfo.orientation]);

  const pageLable = (side: number) => {
    const suffix = side === 1 ? "Front" : "Back";
    const totalCardNum = Math.floor(docDimension.count / 2 + 0.5);
    if (totalCardNum === 1) return suffix;
    return `Card ${cardNum + 1} of ${totalCardNum} - ${suffix}`;
  };

  const copyRightText = (pageLabel: string) => {
    const c1 = <span className={classes.cardPageNum}>{pageLabel}</span>;
    const c2 = (
      <span>{`© Apricad LLC, 2019-${new Date().getFullYear()}. All Rights Reserved.`}</span>
    );
    return (
      <div className={classes.cardFooter}>
        {screenInfo.width >= 768 ? (
          <>
            {c2}
            {c1}
          </>
        ) : (
          <>
            {c1}
            {c2}
          </>
        )}
      </div>
    );
  };

  const content = (side: number) => {
    const pageNum = cardNum * 2 + side;
    return pdfReady ? (
      pageNum > docDimension.count ? (
        <div style={{ width: "100%" }}>
          <p className={classes.blankPage}>This side is blank.</p>
        </div>
      ) : (
        <div className={classes.cardContent}>
          <PdfViewer
            pdfDoc={pdfjsLib && pdfjsLib.getDoc()}
            pdfPageNum={pageNum}
            pdfPageScale={docDimension.scale / 10}
            canvasId={`cardCanvas${side}`}
          />
          {copyRightText(pageLable(side))}
        </div>
      )
    ) : (
      <div style={{ position: "absolute", width: "100%" }}>
        {progressCircles()}
      </div>
    );
  };

  const viewCard = () => {
    const { width, height } = docDimension;
    const click = (e: any) => {
      e.stopPropagation();
      setTriggerFlip(!triggerFlip);
    };

    return (
      <div className={classes.flipCard}>
        <div className={classes.perspective}>
          <div
            id={idCard}
            className={classNames({
              [classes.cardWrap]: true,
              [classes.flip]: triggerFlip,
            })}
            style={{
              width: `calc(${width}px + 1rem)`,
              height: `calc(${height}px + 2rem)`,
            }}>
            {/* Front View: always show the odd number page
                Back View:  always show the even number page
                When flipped, the front goes under, and the back becomes visible.
                */}
            <div className={classes.cardFront} onClick={(e) => click(e)}>
              {content(1)}
            </div>
            <div className={classes.cardBack} onClick={(e) => click(e)}>
              <div className={classes.cardBackContent}>{content(2)}</div>
            </div>
          </div>
          {cardActionBar()}
        </div>
      </div>
    );
  };

  const handleClose = () => onAction({ action: "ibx", str: null });

  const cardCtrl = (
    next: number,
    label: string,
    icon: React.ReactElement,
    id: string,
  ) => {
    if (docDimension.count <= 2 && next !== 0) return <></>;

    const totalCardNum = Math.floor(docDimension.count / 2 + 0.5);
    const disabled =
      (next > 0 && cardNum === totalCardNum - 1) || (next < 0 && cardNum === 0);
    const handler = (e: any) => {
      e.stopPropagation();
      if (next) {
        // When Prev/Next clicked, reset the flip state
        setTriggerFlip(false);
        setCardNum(cardNum + next);
      } else {
        handleClose();
      }
    };

    return screenInfo.width >= 768 ? (
      <Button
        id={id}
        classes={{ root: classes.btn }}
        style={{ textTransform: "none", marginLeft: next === 1 ? "1rem" : 0 }}
        size="large"
        disabled={disabled}
        onClick={(e) => handler(e)}>
        <b>{label}</b>
      </Button>
    ) : (
      <Fab
        id={id}
        classes={{ root: classes.btn }}
        style={{ color: "white", margin: "0 0.5rem" }}
        size={"small"}
        disabled={disabled}
        onClick={(e) => handler(e)}>
        {icon}
      </Fab>
    );
  };

  const cardActionBar = () => {
    return (
      <div
        className={classes.cardActions}
        style={{ width: docDimension.width }}>
        {cardCtrl(0, "Close", <Close />, idClose)}
        <span
          id={idCardFlip}
          onClick={(e) => {
            e.stopPropagation();
            setTriggerFlip(!triggerFlip);
          }}>
          <b>Click Card to Flip</b>
        </span>
        <div>
          {cardCtrl(-1, "Previous Card", <SkipPreviousRounded />, idCardPrev)}
          {cardCtrl(1, "Next Card", <SkipNextRounded />, idCardNext)}
        </div>
      </div>
    );
  };

  return (
    <Dialog
      classes={{ paper: classes.cardPaper, root: classes.cardRoot }}
      fullScreen={true}
      open={true}
      onClose={handleClose}>
      <DialogContent
        classes={{ root: classes.cardRoot }}
        style={{ padding: 0 }}
        onClick={handleClose}>
        {viewCard()}
      </DialogContent>
    </Dialog>
  );
};

function mapStateToProps(state: any) {
  return {
    pdfContent: selectorPDFContent(state),
    screenInfo: state.state_modal.screenInfo,
  };
}

const Base: any = CardViewerBase;
export const CardViewer: any = withStyles(styles)(
  connect(mapStateToProps, { getLicensedDoc })(Base),
);

export const CardViewerTest = withStyles(styles)(Base);
